// Eskom PARCC — SmartList: a compound-row list primitive (owner R3).
//
// Replaces the wide, horizontally-scrolling <table> on the dense surfaces with a
// list of multi-line compound rows (à la a smart inbox): each row packs primary +
// secondary info and inline actions, so everything fits the row width and there is
// NO horizontal scroll. Presentational only — it sorts and renders the rows it is
// given (reusing the table primitive's pure, tested `sortRows`), never fetches.
//
// The caller supplies `renderRow(row)` returning the row's inner content; SmartList
// owns the row chrome: the clickable/selectable wrapper, keyboard activation, the
// empty state, and a compact count + "Sort by" bar.
import { useMemo, useState } from 'react';
import type { ReactNode, KeyboardEvent } from 'react';
import { sortRows, type SortDir } from './DataTable';
import { Select } from './Select';

export interface SortOption<T> {
  key: string;
  label: string;
  accessor: (row: T) => unknown;
}

export function SmartList<T>({
  items,
  getRowKey,
  renderRow,
  sortOptions,
  initialSortKey,
  initialSortDir = 'asc',
  onRowClick,
  selectedRowKey,
  countLabel,
  emptyMessage = 'No records to display.',
}: {
  items: T[];
  getRowKey: (row: T, index: number) => string | number;
  /** The row's inner content (lead cluster, main lines, actions). */
  renderRow: (row: T) => ReactNode;
  /** Sort choices for the bar; omit to hide the sort control. */
  sortOptions?: SortOption<T>[];
  initialSortKey?: string;
  initialSortDir?: SortDir;
  onRowClick?: (row: T) => void;
  selectedRowKey?: string | number;
  /** Left-aligned count/summary text in the bar (e.g. "14 transaction(s)"). */
  countLabel?: ReactNode;
  emptyMessage?: ReactNode;
}): React.JSX.Element {
  const [sortKey, setSortKey] = useState<string | undefined>(
    () => initialSortKey ?? sortOptions?.[0]?.key,
  );
  const [dir, setDir] = useState<SortDir>(initialSortDir);

  const sorted = useMemo(() => {
    const opt = sortOptions?.find((o) => o.key === sortKey);
    if (!opt) return items;
    return sortRows(items, opt.key, dir, opt.accessor);
  }, [items, sortOptions, sortKey, dir]);

  return (
    <div className="smartlist">
      {(countLabel || (sortOptions && sortOptions.length > 0)) && (
        <div className="smartlist-bar">
          <div className="small">{countLabel}</div>
          {sortOptions && sortOptions.length > 0 && (
            <div className="smartlist-sort">
              <label>
                Sort&nbsp;
                <Select
                  value={sortKey ?? ''}
                  onChange={setSortKey}
                  ariaLabel="Sort by"
                  options={sortOptions.map((o) => ({ value: o.key, label: o.label }))}
                />
              </label>
              <button
                type="button"
                className="btn ghost"
                onClick={() => setDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                aria-label={dir === 'asc' ? 'Sort ascending (toggle)' : 'Sort descending (toggle)'}
                title={dir === 'asc' ? 'Ascending' : 'Descending'}
              >
                {dir === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          )}
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="empty">{emptyMessage}</div>
      ) : (
        sorted.map((row, index) => {
          const key = getRowKey(row, index);
          const selected = selectedRowKey !== undefined && key === selectedRowKey;
          const activate = onRowClick ? () => onRowClick(row) : undefined;
          return (
            <div
              key={key}
              className={`srow${selected ? ' is-selected' : ''}`}
              onClick={activate}
              role={activate ? 'button' : undefined}
              tabIndex={activate ? 0 : undefined}
              aria-pressed={activate ? selected : undefined}
              onKeyDown={
                activate
                  ? (e: KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        activate();
                      }
                    }
                  : undefined
              }
            >
              {renderRow(row)}
            </div>
          );
        })
      )}
    </div>
  );
}
